import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactMarkdown from 'react-markdown';
import FileViewer from './FileViewer';
import spreadSheetIcon from '../assets/icons/SpreadsheetIcon.svg';
import rightDesign from '../assets/images/ScaylorCardRightDes.svg';
import leftDesign from '../assets/images/ScaylorCardLeftDes.svg';
import FilterBar from './FilterBar';
import { BsGrid, BsList, BsTable } from 'react-icons/bs';
import ScaylorOrbB from '../assets/images/ScaylorOrbB.svg';

const ViewToggle = ({ view, onViewChange }) => {
  return (
    <div className="flex gap-2 items-center">
      <button
        onClick={() => onViewChange('list')}
        className={`p-2 rounded ${view === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
      >
        <BsList size={20} />
      </button>
      <button
        onClick={() => onViewChange('grid')}
        className={`p-2 rounded ${view === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
      >
        <BsGrid size={20} />
      </button>
      <button
        onClick={() => onViewChange('table')}
        className={`p-2 rounded ${view === 'table' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
      >
        <BsTable size={20} />
      </button>
    </div>
  );
};

const getSimplifiedType = (mimeType) => {
  if (!mimeType) return 'Unknown';
  
  const lowerType = mimeType.toLowerCase();
  
  if (lowerType.includes('spreadsheet') || lowerType.includes('excel') || lowerType.includes('xlsx')) {
    return 'Spreadsheet';
  }
  if (lowerType.includes('pdf')) {
    return 'PDF';
  }
  if (lowerType.includes('presentation') || lowerType.includes('powerpoint')) {
    return 'Presentation';
  }
  if (lowerType.includes('document') || lowerType.includes('word') || lowerType.includes('docx')) {
    return 'Document';
  }
  if (lowerType.includes('image')) {
    return 'Image';
  }
  if (lowerType.includes('text')) {
    return 'Text';
  }
  if (lowerType.includes('video')) {
    return 'Video';
  }
  if (lowerType.includes('audio')) {
    return 'Audio';
  }
  
  // If no specific match, return just the main type
  const mainType = mimeType.split('/')[0];
  if (mainType && mainType !== 'application') {
    return mainType.charAt(0).toUpperCase() + mainType.slice(1);
  }
  
  return 'File';
};

const SearchResultCard = ({ title, type, index, view = 'list', file, selectedContextId }) => {
  const [isFileOpen, setIsFileOpen] = useState(false);
  const simplifiedType = getSimplifiedType(type);

  const handleViewFile = () => {
    setIsFileOpen(true);
  };

  const getTypeIcon = (type) => {
    switch(type.toLowerCase()) {
      case 'document':
        return '';
      default:
        return '';
    }
  };

  // Modified to follow specific pattern
  const CardBackground = ({ index }) => {
    switch(index % 3) {
      case 0: // White card
        return null;
      case 1: // Right design
        return (
          <img
            src={rightDesign}
            alt=""
            className="absolute top-0 right-0 h-full pointer-events-none opacity-100"
          />
        );
      case 2: // Left design
        return (
          <img
            src={leftDesign}
            alt=""
            className="absolute top-0 left-0 h-full pointer-events-none opacity-100"
          />
        );
      default:
        return null;
    }
  };

  if (view === 'grid') {
    return (
      <>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <div className="flex flex-col h-full">
            <div className="text-2xl mb-3">{getTypeIcon(simplifiedType)}</div>
            <h3 className="text-sm font-semibold mb-1 text-blue-600">{title}</h3>
            <div className="text-sm text-gray-600 font-medium mb-2">Type: {simplifiedType}</div>
            <button 
              onClick={handleViewFile}
              className="mt-auto px-4 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors w-full"
            >
              View File
            </button>
          </div>
        </div>
        {isFileOpen && (
          <FileViewer 
            file={file}
            contextId={selectedContextId}
            onClose={() => setIsFileOpen(false)} 
          />
        )}
      </>
    );
  }

  // Original list view with added summary
  return (
    <>
      <div className="bg-white rounded-lg p-6 mb-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow relative overflow-hidden">
        <CardBackground index={index} />
        <div className="flex items-start gap-4 relative z-10">
          <div className="text-2xl">{getTypeIcon(simplifiedType)}</div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold mb-1 text-blue-600">{title}</h3>
            <div className="text-sm text-gray-600 font-medium mb-2">Type: {simplifiedType}</div>
            {file.summary && (
              <div className="text-sm text-gray-600 mb-4 prose prose-sm max-w-none">
                <ReactMarkdown>{file.summary}</ReactMarkdown>
              </div>
            )}
            <button 
              onClick={handleViewFile}
              className="mt-4 px-4 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              View File
            </button>
          </div>
        </div>
      </div>
      {isFileOpen && (
        <FileViewer 
          file={file}
          contextId={selectedContextId}
          onClose={() => setIsFileOpen(false)} 
        />
      )}
    </>
  );
};

const TableView = ({ files, onViewFile, contextId }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[400px]">Summary</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {files.map((file, index) => (
              <tr 
                key={index} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onViewFile(file)}
              >
                <td className="px-6 py-4">
                  <div className="text-sm text-blue-600 hover:underline truncate">{file.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600">{getSimplifiedType(file.type)}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 prose prose-sm max-w-none max-h-24 overflow-hidden">
                    <div className="line-clamp-3">
                      <ReactMarkdown>{file.summary}</ReactMarkdown>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SearchResultCards = forwardRef(({ selectedContextId }, ref) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [view, setView] = useState('table');
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);

  const fetchLatestMessages = async () => {
    if (!selectedContextId) return Promise.resolve();

    const token = localStorage.getItem('token');
    if (!token) return Promise.resolve();

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/${selectedContextId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const messages = await response.json();
        console.log('Messages API Response:', messages);
        
        // Get the latest message's files
        const latestMessage = messages[messages.length - 1];
        if (latestMessage && latestMessage.files) {
          setFiles(latestMessage.files);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchLatestMessages
  }));

  useEffect(() => {
    fetchLatestMessages();
  }, [selectedContextId]);

  const filteredFiles = files.filter(file => 
    activeFilter === 'all' || getSimplifiedType(file.type) === activeFilter
  );

  const handleViewFile = (file) => {
    setSelectedFile(file);
  };

  if (!selectedContextId) return null;

  return (
    <div className="mt-6 max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <FilterBar 
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
        <ViewToggle view={view} onViewChange={setView} />
      </div>
      
      {files.length > 0 && (
        <>
          {view === 'table' ? (
            <TableView files={filteredFiles} onViewFile={handleViewFile} contextId={selectedContextId} />
          ) : (
            <div className={`
              ${view === 'grid' 
                ? 'grid grid-cols-2 gap-4 md:grid-cols-3' 
                : 'space-y-4'
              }
            `}>
              {filteredFiles.map((file, index) => (
                <SearchResultCard 
                  key={index}
                  title={file.name}
                  type={file.type}
                  index={index}
                  view={view}
                  file={file}
                  selectedContextId={selectedContextId}
                />
              ))}
            </div>
          )}
        </>
      )}

      {selectedFile && (
        <FileViewer 
          file={selectedFile}
          contextId={selectedContextId}
          onClose={() => setSelectedFile(null)} 
        />
      )}
    </div>
  );
});

export default SearchResultCards; 